import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useError } from '../contexts/ErrorContext';
import { fetchBleSerialLogUploads } from '../services/reportsService';
import AnalyticsTimezoneNote from './AnalyticsTimezoneNote';
import { sortVersionsDesc } from '../utils/versionSort';
import { ANALYTICS_TIMEZONE, wallTimeToInstant, formatInTimeZone } from '../utils/timezone';
import './BleSerialLogUploadsReport.css';

const today = new Date().toISOString().slice(0, 10);
const defaultStart = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
})();

const PLATFORM_LABELS = { IOS: 'iOS', ANDROID: 'Android' };

const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allSelected = selected.length === options.length;
  const noneSelected = selected.length === 0;

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const buttonLabel = noneSelected
    ? `${label}: None`
    : allSelected
      ? `${label}: All`
      : `${label}: ${selected.length}/${options.length}`;

  return (
    <div className="bl-multiselect" ref={ref}>
      <button className="bl-multiselect-btn" onClick={() => setOpen(!open)}>
        {buttonLabel}
        <span className={`bl-multiselect-chevron ${open ? 'open' : ''}`}>&#9662;</span>
      </button>
      {open && (
        <div className="bl-multiselect-dropdown">
          <div className="bl-multiselect-actions">
            <button onClick={() => onChange([...options])}>All</button>
            <button onClick={() => onChange([])}>None</button>
          </div>
          <div className="bl-multiselect-options">
            {options.map(opt => (
              <label key={opt} className="bl-multiselect-option">
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Deep link into the user page's analytics tab with this capture's session
// pre-selected. Absolute instants + `tz` so the receiving page shows the same
// window regardless of the viewer's own zone.
const buildSessionHref = (capture) => {
  if (!capture.user_id) return null;
  const endDay = capture.end_day && capture.end_day > capture.day ? capture.end_day : capture.day;
  const start = wallTimeToInstant(`${capture.day}T00:00`, ANALYTICS_TIMEZONE);
  const end = wallTimeToInstant(`${endDay}T23:59`, ANALYTICS_TIMEZONE);
  if (!start || !end) return `/users/${capture.user_id}`;
  const params = new URLSearchParams({
    tab: 'analytics',
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    tz: ANALYTICS_TIMEZONE,
  });
  if (capture.session_id) params.set('session', capture.session_id);
  return `/users/${capture.user_id}?${params.toString()}`;
};

const BleSerialLogUploadsReport = () => {
  const { addError } = useError();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Filter options, populated from the first unfiltered fetch for the range
  const [allIosVersions, setAllIosVersions] = useState([]);
  const [allAndroidVersions, setAllAndroidVersions] = useState([]);
  const [allFirmwareVersions, setAllFirmwareVersions] = useState([]);
  const [selectedIosVersions, setSelectedIosVersions] = useState([]);
  const [selectedAndroidVersions, setSelectedAndroidVersions] = useState([]);
  const [selectedFirmware, setSelectedFirmware] = useState([]);

  // A selection only applies to the range it was populated from.
  const resetFilters = () => {
    setAllIosVersions([]);
    setAllAndroidVersions([]);
    setAllFirmwareVersions([]);
    setSelectedIosVersions([]);
    setSelectedAndroidVersions([]);
    setSelectedFirmware([]);
  };

  const runQuery = useCallback(async (useCurrentFilters) => {
    if (!startDate || !endDate) return;
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before or equal to end date.');
      return;
    }

    // Every app version or every firmware deselected can only return zero rows.
    const hasOptions = allIosVersions.length > 0 || allAndroidVersions.length > 0;
    if (useCurrentFilters && hasOptions && (
      (selectedIosVersions.length === 0 && selectedAndroidVersions.length === 0)
      || selectedFirmware.length === 0
    )) {
      setData(prev => prev && { ...prev, captures: [] });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const filters = useCurrentFilters
        ? {
          iosVersions: selectedIosVersions,
          androidVersions: selectedAndroidVersions,
          gogglesFirmwareVersions: selectedFirmware,
        }
        : null;
      const result = await fetchBleSerialLogUploads(startDate, endDate, addError, filters);
      setData(result);

      if (!useCurrentFilters) {
        const iosVers = sortVersionsDesc(result.availableVersions?.ios || []);
        const androidVers = sortVersionsDesc(result.availableVersions?.android || []);
        const fwVers = sortVersionsDesc(result.availableVersions?.firmware || []);
        setAllIosVersions(iosVers);
        setAllAndroidVersions(androidVers);
        setAllFirmwareVersions(fwVers);
        setSelectedIosVersions(iosVers);
        setSelectedAndroidVersions(androidVers);
        setSelectedFirmware(fwVers);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch BLE serial log uploads.');
    } finally {
      setLoading(false);
    }
  }, [
    startDate, endDate, addError, selectedIosVersions, selectedAndroidVersions, selectedFirmware,
    allIosVersions.length, allAndroidVersions.length,
  ]);

  const handleRunReport = () => runQuery(false);
  const handleApplyFilters = () => runQuery(true);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleRunReport();
  };

  const handleDateChange = (setter) => (e) => {
    setter(e.target.value);
    resetFilters();
  };

  const hasVersionOptions = allIosVersions.length > 0 || allAndroidVersions.length > 0 || allFirmwareVersions.length > 0;
  const captures = data?.captures || [];

  return (
    <div className="bl-report">
      <div className="bl-header">
        <div className="bl-header-left">
          <div>
            <h1 className="bl-title">BLE Serial Log Uploads</h1>
            <p className="bl-subtitle">Goggles serial logs captured over BLE and uploaded by the app</p>
          </div>
        </div>
        <div className="bl-controls">
          <input
            className="bl-date-input"
            type="date"
            value={startDate}
            onChange={handleDateChange(setStartDate)}
            onKeyDown={handleKeyDown}
          />
          <span className="bl-date-sep">to</span>
          <input
            className="bl-date-input"
            type="date"
            value={endDate}
            onChange={handleDateChange(setEndDate)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="bl-fetch-btn"
            onClick={handleRunReport}
            disabled={loading || !startDate || !endDate}
          >
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>

      <AnalyticsTimezoneNote timezone={data?.timezone} />

      {hasVersionOptions && (
        <div className="bl-filters">
          <MultiSelect
            label="iOS App Version"
            options={allIosVersions}
            selected={selectedIosVersions}
            onChange={setSelectedIosVersions}
          />
          <MultiSelect
            label="Android App Version"
            options={allAndroidVersions}
            selected={selectedAndroidVersions}
            onChange={setSelectedAndroidVersions}
          />
          <MultiSelect
            label="Goggles Firmware"
            options={allFirmwareVersions}
            selected={selectedFirmware}
            onChange={setSelectedFirmware}
          />
          <button className="bl-filter-apply" onClick={handleApplyFilters} disabled={loading}>
            Apply Filters
          </button>
        </div>
      )}

      {error && <div className="bl-error">{error}</div>}

      {loading && (
        <div className="bl-loading">
          <div className="bl-spinner" />
          Querying BigQuery...
        </div>
      )}

      {!loading && !data && !error && (
        <div className="bl-empty">Select a date range and click Run Report.</div>
      )}

      {!loading && data && (
        <div className="bl-content">
          <div className="bl-table-section">
            <h3 className="bl-section-title">Successful BLE Log Capture ({captures.length})</h3>
            {captures.length === 0 ? (
              <div className="bl-empty-filtered">No successful log captures in this range.</div>
            ) : (
              <div className="bl-table">
                <div className="bl-table-row bl-table-head">
                  <span>User</span>
                  <span>Date</span>
                  <span>App Version</span>
                  <span>Goggles Version</span>
                  <span>Timestamp</span>
                </div>
                {captures.map((c) => {
                  const href = buildSessionHref(c);
                  const RowTag = href ? 'a' : 'div';
                  const rowProps = href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {};
                  return (
                    <RowTag
                      key={`${c.user_identifier}-${c.timestamp}`}
                      className="bl-table-row"
                      {...(c.path_to_log && { title: c.path_to_log })}
                      {...rowProps}
                    >
                      <span className="bl-user-name">
                        <span>{c.user_name || 'Unknown user'}</span>
                        <span className="bl-user-uid">{c.user_id || c.user_identifier}</span>
                      </span>
                      <span>
                        {formatInTimeZone(c.timestamp, ANALYTICS_TIMEZONE, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                      <span>
                        {c.app_version}
                        <span className="bl-platform">{PLATFORM_LABELS[c.platform] || c.platform}</span>
                      </span>
                      <span>{c.goggles_sw_version}</span>
                      <span>
                        {formatInTimeZone(c.timestamp, ANALYTICS_TIMEZONE, {
                          year: 'numeric', month: 'short', day: 'numeric',
                          hour: 'numeric', minute: '2-digit', second: '2-digit',
                        })}
                      </span>
                    </RowTag>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BleSerialLogUploadsReport;
